import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { EmptyState, PageSkeleton } from "@/components/ui";
import { IntakeWorkspace } from "@/components/change-requests/intake-workspace";
import { SingleRequestPage } from "@/components/change-requests/single-request-page";
import { useChangeRequestSet } from "@/hooks/use-change-requests";

export const Route = createFileRoute("/_authenticated/test-activity/change-requests/$setId/")({
  component: ChangeRequestSetRoute,
});

/**
 * The link target for a change-request set from every surface. It reads the
 * review model once and renders exactly one outcome: a set that rode a test
 * is decided on its submission's page (the URL is replaced — no history
 * entry); an intake, or a standalone set carrying the intake it was sent
 * with, is the intake workspace; any other set is the single-request page.
 * The route owns the read's loading, 404, 403 and error states; a page
 * mounts only once the review has loaded.
 */
function ChangeRequestSetRoute() {
  const { t } = useTranslation("change-requests");
  const { setId } = Route.useParams();
  const query = useChangeRequestSet(setId);
  const review = query.data;

  if (!review) {
    if (!query.isError) {
      return (
        <div className="p-8">
          <PageSkeleton />
        </div>
      );
    }
    const status = query.error instanceof ApiError ? query.error.status : null;
    return (
      <div className="p-8">
        {status === 403 ? (
          <EmptyState title={t("states.forbidden")} />
        ) : status === 404 ? (
          <EmptyState title={t("states.loadError")} />
        ) : (
          <EmptyState
            title={t("states.loadError")}
            actionLabel={t("states.retry")}
            onAction={() => void query.refetch()}
          />
        )}
      </div>
    );
  }
  if (review.anchor_kind === "submission" && review.submission_id !== null) {
    return (
      <Navigate
        to="/test-activity/submissions/$id"
        params={{ id: review.submission_id }}
        hash="change-requests"
        replace
      />
    );
  }
  return review.intake ? (
    <IntakeWorkspace review={review} intake={review.intake} query={query} />
  ) : (
    <SingleRequestPage review={review} query={query} />
  );
}
